import { QueryClient } from '@tanstack/vue-query'

export const queryClient = new QueryClient()

/**
 * Generic url fetcher.
 *
 * @param string url - an url to call
 * @param string address - a method used in the request, either 'GET' or 'POST'
 * @param RequestType data - a request data to be sent
 * @param Record headers - additional headers to be sent
 * @param timeout optional timeout in milliseconds. If specified will use AbortController and will throw AbortError in case of a timeout.
 * @returns a result from calling the url
 * @throws {ethErrors.rpc.internal} - throws if response is not 20*
 */
interface FetcherConfig<T> {
  url: string
  method: 'GET' | 'POST'
  data?: T
  headers?: Record<string, string>
  timeout?: number
}

async function fetchData<ResponseType, RequestType>({
  url,
  method,
  data,
  headers,
  timeout,
}: FetcherConfig<RequestType>): Promise<ResponseType> {
  const fetchConfig: RequestInit = {
    method,
    headers: headers || {
      Accept: 'application/json',
      ...(data ? { 'Content-Type': 'application/json' } : {}),
    },
    body: method === 'POST' ? JSON.stringify(data) : undefined,
  }

  let response: Response
  if (timeout) {
    const controller = new AbortController()
    const id = setTimeout(() => controller.abort(), timeout)
    response = await fetch(url, {
      ...fetchConfig,
      signal: controller.signal,
    })
    clearTimeout(id)
  } else {
    response = await fetch(url, fetchConfig)
  }

  if (!response.ok) {
    return response
      .json()
      .catch(() => {
        throw new Error(response?.status?.toString() ?? 'Unknown error')
      })
      .then(message => {
        throw new Error(
          'Failed to fetch data. Error response successfully parsed to JSON.',
          { cause: message }
        )
      })
  }
  return await response.json()
}

/**
 * Fetcher function to be used in the app.
 *
 * @param config - FetcherConfig
 * @param useTanstack - boolean to use tanstack
 * @param cacheData - boolean to cache data
 * @returns - a result from calling the url
 */
export const fetcher = async <ResponseType, RequestType>(
  config: FetcherConfig<RequestType>,
  useTanstack = false,
  cacheData = false
): Promise<ResponseType> => {
  return useTanstack
    ? await queryClient.fetchQuery<ResponseType>({
        queryKey: ['fetcher', config.url, config.method],
        queryFn: () => fetchData(config),
        staleTime:
          cacheData && process.env.NODE_ENV !== 'test' ? 10 * 60 * 1000 : 0,
        retry: 3,
        retryDelay: process.env.NODE_ENV === 'test' ? 0 : undefined,
      })
    : await fetchData(config)
}
